import { memo, useEffect, useState } from 'react'
import { GetServerSidePropsContext, NextPage } from 'next'
import { Router, useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import classNames from 'classnames'
import { getApolloClient } from 'graphql/apolloClient'
import { useQuery } from '@apollo/client'
import {
  CLOUDFRONT_CITY_HEADER,
  DEFAULT_CITY_SLUG,
  DEFAULT_STATE_SLUG,
} from 'config'
import { nameFromSlug, slugify } from 'lib/text'
import {
  PAGE_SIZE,
  getVariablesProperties,
  TQueryParams,
  getRouterProperties,
  TypedUrlQueries,
  getTitle,
  getCanonicalURL,
  getDescription,
  metaImage,
  getFiltersCount,
  transformSearchCollections,
  getAppliedFilters,
  getFullAddress,
  RECOMMENDATIONS_BANNER_SIZE,
  getListingAddress,
} from 'lib/search'
import logger from 'lib/logger'
import {
  fetchGeocodeByAddress,
  parseGeocode,
  parseLocation,
} from 'lib/location'
import { clearApolloStore } from 'lib/user'
import useMap from 'hooks/useMap'
import useGeoLocation from 'hooks/useGeoLocation'
import useMediaQuery from 'hooks/useMediaQuery'
import useLogin from 'hooks/useLogin'
import GET_SEARCH_LISTING from 'graphql/queries/searchListings'
import GET_SEARCH_CONTEXT from 'graphql/queries/searchContext'
import GET_SEARCH_ADDRESS_CONTEXT from 'graphql/queries/searchAddressContext'
import GET_BUYER_CONTEXT_REGIONS from 'graphql/queries/buyerContextRegions'
import GET_CITIES from 'graphql/queries/cities'
import {
  City,
  CitiesQuery,
  GetSearchContextQueryVariables,
  District,
  GetSearchContextQuery,
  Listing,
  SearchListingsQuery,
  SearchListingsQueryVariables,
  Tag,
  Collection,
  BuyerContextRegionsQuery,
  BuyerContextRegionsQueryVariables,
  Resolution,
  Point,
  Condominium,
  GetSearchAddressContextQuery,
  GetSearchAddressContextQueryVariables,
} from 'graphql/types'
import { PlaceSchema } from 'config/schemas'
import { LOCALSTORAGE_SEARCH_QEV_MODAL_KEY, useQEVModal } from 'components/QEV'
import DefaultTemplate from 'templates/Default'
import Pagination from 'components/Pagination'
import { IconFilter } from 'icons'
import { TItem } from 'components/Typeahead'
import PreFooter from 'components/PreFooter'
import Tabs, { TTabProps } from 'components/navigation/Tabs'
import FiltersButton from 'components/FiltersButton'
import PreFooterSearchLinks from 'components/PreFooter/components/PreFooterSearchLinks'
import BreadcrumbSection from 'components/PreFooter/components/BreadcrumbSection'
import SearchGridList from './components/SearchGridList'
import SearchTitle from './components/SearchTitle'
import type { TSelectType } from './components/TypeaheadSearch'
import ToggleView from './components/ToggleView'
import OrderBy from './components/OrderBy'
import RecommendationsCarousel from './components/RecommendationsCarousel'
import * as searchEvents from './events'
import styles from './Search.module.scss'

const FiltersModal = dynamic(() => import('components/FiltersModal'))
const CollectionDefault = dynamic(
  () => import('./components/Collections/CollectionDefault'),
)
const TypeaheadSearch = dynamic(() => import('./components/TypeaheadSearch'))
const NeighborhoodInfo = dynamic(
  () => import('components/PreFooter/components/NeighborhoodInfo'),
)
const CityInfo = dynamic(() => import('./components/CityInfo/CityInfo'))
const EmptySearch = dynamic(() => import('./components/EmptySeach'))
const ListingsMap = dynamic(() => import('./components/ListingsMap'))
const QEV = dynamic(() => import('components/QEV'))
const LoginModal = dynamic(() => import('components/LoginModal'))

type TProps = {
  serverRegionsData: BuyerContextRegionsQuery
  serverSearchData: SearchListingsQuery
  serverLoading?: boolean
  serverContext: {
    districts: District[]
    tags: Tag[]
    searchCollections: Collection[]
    pointsOfInterest: Point[]
    condominiums?: Array<Condominium>
  }
  serverCities: City[]
  serverFilterVariables: TQueryParams
  serverSearchVariables?: SearchListingsQueryVariables
}

const Search: NextPage<TProps> = ({
  serverSearchData,
  serverRegionsData,
  serverLoading,
  serverContext,
  serverCities,
  serverFilterVariables,
  serverSearchVariables,
}) => {
  const { userProfile } = useLogin()
  const router = useRouter()
  const { state, city } = router.query
  const isMapRoute = router?.asPath?.includes('/mapa')
  const isMiniMobileResolution = useMediaQuery('(max-width: 359px)')
  const isTabletResolution = useMediaQuery('(min-width: 768px)')
  const isDeskResolution = useMediaQuery('(min-width: 1024px)')

  const [isSearchByLocation, setIsSearchByLocation] = useState(false)
  const { location, getLocation } = useGeoLocation({ update: false })
  const [loading, setLoading] = useState(false)
  const [isMounted, setMounted] = useState(false)

  const [filterModal, { setAll: setAllFilterModal }] = useMap({
    isMounted: false,
    isVisible: false,
  })

  const [loginModal, { setAll: setLoginModal }] = useMap({
    isMounted: false,
    isVisible: false,
  })

  const [clientQueryVariables, query] = useMap<TQueryParams>(
    serverFilterVariables,
  )
  const queryVariables = serverFilterVariables || clientQueryVariables
  const filtersCount = getFiltersCount(queryVariables)
  const { QEVModal, setQEVModal } = useQEVModal()

  const { data: recommendationsData, loading: recommendationsDataLoading } =
    useQuery<SearchListingsQuery, SearchListingsQueryVariables>(
      GET_SEARCH_LISTING,
      {
        ssr: false,
        variables: {
          collection: 'ideal-para-voce',
          pagination: {
            size: 3,
          },
        },
      },
    )

  const recommendations = recommendationsData?.searchListings

  const cities = serverCities || []
  const {
    name: cityName,
    state: cityState,
    lat: cityLat,
    lng: cityLng,
  } = cities.find((c) => c.nameSlug === city) || {
    state: ((state as string) || '').toUpperCase(),
    name: nameFromSlug(city as string),
  }

  const properties = serverContext || {}
  const currentURL = router.asPath.split('?')[0]
  const currentCollection = properties.searchCollections?.find((collection) =>
    currentURL.split('/').includes(collection?.nameSlug as string),
  )
  const currentPointOfInterest = properties.pointsOfInterest?.find((point) =>
    currentURL.split('/').includes(point?.nameSlug as string),
  )

  const currentCondominium = properties.condominiums?.find((condominium) =>
    currentURL.split('/').includes(condominium?.nameSlug as string),
  )

  const search = serverSearchData?.searchListings

  const hasSearchListingsResults =
    search?.listings && search?.listings?.length > 0

  const collectionsItems = transformSearchCollections({
    collections: properties.searchCollections,
    currentCollection: currentCollection?.nameSlug as string,
    onClick: (collection) => {
      query.setAll({
        ...queryVariables,
        collection: collection?.nameSlug as string,
        pagination: 1,
      })

      searchEvents.searchCollectionClick(collection?.name as string)
    },
  })

  const [lastSelectedTag, setLastSelectedTag] = useState('')

  const onClearTypeahead = () => {
    query.setAll({
      ...queryVariables,
      lc: '',
      neighborhoods: '',
      streetSlug: '',
      pointOfInterest: '',
      condominium: '',
    })
    searchEvents.searchInputClearEvent()
  }

  const onChangePage = (from: string, page: number) => {
    query.set('pagination', page)
    searchEvents.paginateEvent({
      page,
      pagination: page,
      itemsPerPage: PAGE_SIZE,
      type: from,
      collection: currentCollection?.name as string,
    })
  }

  const onSelectSuggestions = (item: TItem, type: TSelectType) => {
    if (type === 'places') {
      void searchEvents.searchInputSelectEvent(item.value)
      void fetchGeocodeByAddress(item.value)
        .then((geocode) => {
          if (geocode) {
            const {
              city: cityFromGeocode,
              citySlug,
              state: stateFromGeocode,
              stateSlug,
              neighborhood,
              street,
              streetSlug,
              postalCode,
              coordinates,
            } = parseGeocode(geocode)

            void searchEvents.searchLocationInputChangeEvent({
              city: cityFromGeocode,
              citySlug,
              state: stateFromGeocode,
              stateSlug,
              neighborhood,
              street,
              streetSlug,
              place: item.value,
              postalCode,
              ...coordinates,
            })

            query.setAll({
              ...queryVariables,
              stateSlug: stateSlug as string,
              citiesSlug: citySlug as string,
              lc: `${coordinates?.lng as number}, ${
                coordinates?.lat as number
              }, ${item.label}`,
              neighborhoods: slugify(neighborhood as string),
              streetSlug: slugify(street as string),
              pagination: 1,
            })
          }
        })
        .catch((e) => logger.capture(e))

      return
    }

    const [stateSlug, nameSlug] = item.value.split('/')

    void searchEvents.searchInputSelectCityEvent(item.value)
    void searchEvents.searchLocationInputChangeEvent({
      city: item.label,
      citySlug: nameSlug,
      state: stateSlug.toUpperCase(),
      stateSlug,
    })

    query.setAll({
      ...queryVariables,
      stateSlug,
      citiesSlug: nameSlug,
      neighborhoods: '',
      streetSlug: '',
      lc: '',
      pointOfInterest: '',
      condominium: '',
      pagination: 1,
    })
  }

  const onSelectLocation = () => {
    searchEvents.searchInputSelectMyLocationEvent()
    getLocation()
    setIsSearchByLocation(true)
  }

  useEffect(() => {
    if (isSearchByLocation && location?.stateSlug && location?.citySlug) {
      query.setAll({
        ...queryVariables,
        stateSlug: location?.stateSlug,
        citiesSlug: location?.citySlug,
        lc: `${location?.coordinates?.lng as number}, ${
          location?.coordinates?.lat as number
        }`,
        neighborhoods: '',
        streetSlug: '',
        pagination: 1,
      })

      void searchEvents.searchInputSelectMyLocationSelectEvent(location)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchByLocation, location])

  useEffect(() => {
    if (isMounted) {
      const routeProps = getRouterProperties(
        clientQueryVariables as TypedUrlQueries,
      )

      void router.push(
        { ...routeProps, hash: window.location.hash },
        undefined,
        {
          scroll: true,
        },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(clientQueryVariables)])

  useEffect(() => {
    if (serverFilterVariables?.isMap !== clientQueryVariables?.isMap)
      query.set('isMap', serverFilterVariables?.isMap)
  }, [serverFilterVariables?.isMap])

  useEffect(() => {
    const totalCount = serverSearchData?.searchListings?.totalCount || 0
    if (totalCount > 0) {
      const filters = getAppliedFilters({
        queryParams: queryVariables,
      })
      searchEvents.searchResultEvent({
        city: cityName as string,
        region: state as string,
        filters: filters as TQueryParams,
        filtersCount,
        listings: serverSearchData?.searchListings?.listings as Listing[],
        itemsCount: totalCount,
        itemsDisplayed: serverSearchData?.searchListings?.listings?.length,
        collection: currentCollection?.name as string,
        pagetype: 'searchresults',
      })
    }
  }, [serverSearchData])

  useEffect(() => {
    const localStorageItem = localStorage.getItem(
      LOCALSTORAGE_SEARCH_QEV_MODAL_KEY,
    )

    if (localStorageItem) {
      if (!userProfile?.completedAboutYou) {
        const localStorageQEV = JSON.parse(localStorageItem) as {
          maxPrice?: string
          citySlug?: string
        }

        const localStorageQEVCity = cities.find(
          ({ nameSlug }) => nameSlug === localStorageQEV?.citySlug,
        )

        setQEVModal({
          isMounted: true,
          isVisible: true,
          defaultValues: {
            maxValue: localStorageQEV?.maxPrice || '',
            city: {
              label: localStorageQEVCity?.name || '',
              value: localStorageQEVCity?.nameSlug || '',
            },
            state: localStorageQEVCity?.state || '',
            neighborhoods: [],
            name: '',
            phoneNumber: '',
            email: '',
          },
        })

        searchEvents.openQEVSearchEvent()
      }

      localStorage.removeItem(LOCALSTORAGE_SEARCH_QEV_MODAL_KEY)
    }
    // Force redirect to refetch queries with user-token
    if (userProfile && isMounted) void router.replace(router.asPath)
  }, [JSON.stringify(userProfile)])

  useEffect(() => {
    setMounted(true)
    searchEvents.openSearchEvent({
      filtersCount,
    })
    const start = (url: string) => {
      // Will match any string that starts with any characters that don't contain "id-", followed by "/imoveis/", followed by any characters.
      if (url.match(/^(?!.*id-).*\/imoveis\/.*$/)) {
        setLoading(true)
      }
    }
    const end = () => {
      setLoading(false)
    }
    Router.events.on('routeChangeStart', start)
    Router.events.on('routeChangeComplete', end)
    Router.events.on('routeChangeError', end)
    return () => {
      Router.events.off('routeChangeStart', start)
      Router.events.off('routeChangeComplete', end)
      Router.events.off('routeChangeError', end)
    }
  }, [])

  const neighborhoodInfo = properties?.districts?.find(
    (d) =>
      d?.nameSlug === queryVariables.neighborhoods ||
      d?.name === queryVariables.neighborhoods,
  )
  const streetName = nameFromSlug(queryVariables.streetSlug as string)

  const listingsTitle = getTitle({
    pointOfInterest: currentPointOfInterest,
    collection: currentCollection,
    queryVariables,
    tags: properties.tags,
    lastSelectedTag,
    condominium: currentCondominium as Condominium,
  })

  const completeAddress = {
    address: {
      state: cityState,
      stateSlug: router.query.state as string,
      city: cityName,
      citySlug: router.query.city as string,
      neighborhood: neighborhoodInfo?.name,
      neighborhoodSlug: neighborhoodInfo?.nameSlug,
      street: streetName,
      streetSlug: queryVariables.streetSlug,
      condominium: currentCondominium,
    },
  }

  const canonicalURL = getCanonicalURL({
    collection: currentCollection,
    queryVariables,
    tags: properties.tags,
    lastSelectedTag,
    address: completeAddress.address,
    pointOfInterest: currentPointOfInterest,
  })

  const isValidAddress =
    completeAddress?.address.city &&
    completeAddress?.address.citySlug &&
    completeAddress?.address.state &&
    completeAddress?.address.stateSlug

  const QEVDefaultValues = {
    maxValue: queryVariables.maxPrice || '',
    city: {
      label: completeAddress.address.city || '',
      value: completeAddress.address.citySlug || '',
    },
    state: completeAddress.address.state || '',
    neighborhoods: [],
    name: '',
    phoneNumber: '',
    email: '',
  }

  const renderResult = () => {
    if (!queryVariables.isMap && (loading || search === undefined)) {
      return (
        <>
          <div className={styles.ecSearchTitle}>
            <SearchTitle loading />
          </div>
          <SearchGridList loading />
        </>
      )
    }

    if (currentCollection && !currentCollection?.enabled) {
      return <CollectionDefault />
    }

    const getCurrentView = () => {
      if (isMapRoute) {
        return (
          <ListingsMap
            filtersCount={filtersCount}
            variablesContext={
              serverSearchVariables as SearchListingsQueryVariables
            }
            initialCenter={{
              lat: cityLat as number,
              lng: cityLng as number,
            }}
            onActionClick={() => {
              setAllFilterModal({
                isMounted: true,
                isVisible: true,
              })
            }}
          />
        )
      }

      if (hasSearchListingsResults) {
        return (
          <>
            <SearchGridList
              loading={loading || serverLoading}
              listings={search.listings as Array<Listing>}
              recommendationsLength={Math.min(
                recommendations?.totalCount as number,
                15,
              )}
              showRecommendationsBanner={queryVariables.pagination === 1}
            />
            <div className={styles.ecSearchPagination}>
              <Pagination
                pages={{
                  total:
                    search?.totalCount && search?.totalCount >= PAGE_SIZE
                      ? Math.ceil(
                          (search.totalCount + RECOMMENDATIONS_BANNER_SIZE) /
                            PAGE_SIZE,
                        )
                      : 1,
                  current: queryVariables.pagination,
                  length: isTabletResolution ? 5 : 3,
                }}
                onClick={(page) => onChangePage('button', page)}
                onNextClick={(page) => onChangePage('next', page)}
                onPrevClick={(page) => onChangePage('prev', page)}
                showJumpPrevActions={!isMiniMobileResolution}
                showJumpNextActions={!isMiniMobileResolution}
                showJumpNextActionsOnFirstPagination={isMiniMobileResolution}
                showJumpPrevActionsOnLastPagination={isMiniMobileResolution}
                size={isDeskResolution ? 'medium' : 'small'}
              />
            </div>
          </>
        )
      }

      return (
        <EmptySearch
          hasSearchPreferences={userProfile?.hasSearchPreferences}
          filtersCount={filtersCount}
          filters={serverSearchVariables?.filters}
          collection={currentCollection?.name as string}
          clientQueryVariables={clientQueryVariables}
          onShow={() =>
            searchEvents.searchNotFoundEvent({
              filters: queryVariables,
              filtersCount,
              collection: currentCollection?.name as string,
            })
          }
          onButtonClick={(hasSearchPreferences) => {
            if (hasSearchPreferences) {
              searchEvents.searchNotFoundCtaEvent({
                filters: queryVariables,
                collection: currentCollection?.name as string,
              })
            } else {
              setQEVModal({
                isMounted: true,
                isVisible: true,
              })
              searchEvents.searchNotFoundQEVCtaEvent({
                filters: queryVariables,
                collection: currentCollection?.name as string,
              })
            }
          }}
        />
      )
    }

    return (
      <>
        {isMapRoute || hasSearchListingsResults ? (
          <div
            className={classNames({
              [styles.ecSearchTitle]: !isMapRoute,
              [styles.ecSearchTitleMap]: isMapRoute,
            })}
          >
            {!isMapRoute ? (
              <SearchTitle
                loading={loading}
                total={search?.totalCount}
                title={listingsTitle}
                location={getListingAddress(completeAddress.address)}
              />
            ) : null}
            {(isMapRoute || (!isMapRoute && hasSearchListingsResults)) && (
              <div className={styles.ecSearchTitleButtonsContainer}>
                {(isMapRoute || hasSearchListingsResults) && (
                  <ToggleView
                    queryVariables={queryVariables}
                    onClick={() => {
                      searchEvents.searchToggleView(
                        queryVariables.isMap ? 'map' : 'list',
                      )
                    }}
                  />
                )}
                {!isMapRoute && hasSearchListingsResults && (
                  <OrderBy
                    value={queryVariables.orderBy}
                    queryVariables={queryVariables}
                    onChange={query.setAll}
                  />
                )}
              </div>
            )}
          </div>
        ) : null}
        {getCurrentView()}
      </>
    )
  }

  return (
    <DefaultTemplate
      head={{
        title: `${listingsTitle} em ${getListingAddress(
          completeAddress.address,
          true,
        )} | EmCasa`,
        description: getDescription({
          ...queryVariables,
          city: cityName as string,
          neighborhood: neighborhoodInfo?.name as string,
          street: streetName,
          condominium: currentCondominium?.name as string,
          pointOfInterest: currentPointOfInterest?.name as string,
          tags: properties.tags,
          lastSelectedTag,
        }),
        url: `https://emcasa.com${router.asPath}`,
        image: metaImage,
        canonical: `https://emcasa.com${canonicalURL}`,
      }}
      noFooter={isMapRoute}
      includeMap
      headerProps={{
        hideLogo: !isDeskResolution,
        borderBottom: true,
        CenteredElement: (
          <div className={styles.ecSearchTypeahead}>
            <TypeaheadSearch
              showOpenButton
              onOpenButtonClick={() =>
                searchEvents.searchLocationOpenButtonEvent()
              }
              cities={cities}
              onSelect={onSelectSuggestions}
              onFocus={searchEvents.searchInputFocusEvent}
              onClear={onClearTypeahead}
              onSelectLocation={onSelectLocation}
            />
          </div>
        ),
      }}
      customClass={{
        main: styles.ecMainSearch,
      }}
    >
      <script
        type="application/ld+json"
        data-testid="placeSchemaScript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            PlaceSchema({
              name: getFullAddress({
                city: cityName as string,
                neighborhood: neighborhoodInfo?.name as string,
                street: streetName,
              }),
              lat: cityLat as number,
              lng: cityLng as number,
              addressLocality: cityName as string,
              addressRegion: cityState as string,
              streetAddress: streetName,
            }),
          ),
        }}
      />
      <div className={styles.ecSearchPageHeader} data-testid="ecSearchPage">
        <div className={styles.ecSearchPageHeaderContainer}>
          <FiltersButton
            appliedFilters={filtersCount}
            buttonElementProps={{
              size: 'small',
              Icon: {
                Component: IconFilter,
                color: 'white',
                title: 'Ícone: filtrar',
              },
              onClick: () => {
                searchEvents.searchFilterOpenEvent()
                setAllFilterModal({
                  isMounted: true,
                  isVisible: true,
                })
              },
            }}
          />

          <Tabs
            size="small"
            scrollAnimation
            activeKey={currentCollection?.nameSlug as string}
            items={collectionsItems as TTabProps[]}
            navigation={{
              hide: !isDeskResolution,
              onScrollToNext: () =>
                searchEvents.searchCollectionPaginate('next'),
              onScrollToPrev: () =>
                searchEvents.searchCollectionPaginate('prev'),
            }}
            className={styles.ecSearchPageTabs}
          />
        </div>
      </div>

      {renderResult()}

      {!isMapRoute && userProfile && (
        <RecommendationsCarousel
          isLoading={recommendationsDataLoading}
          recommendations={recommendations}
        />
      )}

      {!isMapRoute && (isValidAddress || neighborhoodInfo?.description) && (
        <div className={styles.ecSearchPagePreFooterWrapper}>
          <PreFooter>
            <BreadcrumbSection breadcrumbListing={completeAddress} />
            <PreFooterSearchLinks
              address={completeAddress.address}
              itemsPerColumn={6}
              totalColumns={1}
              regions={serverRegionsData}
              isLoading={loading}
            />
            <NeighborhoodInfo {...neighborhoodInfo} />
          </PreFooter>
        </div>
      )}

      {!isMapRoute && !completeAddress.address.neighborhood ? (
        <CityInfo address={completeAddress.address} />
      ) : null}

      {filterModal.isMounted && (
        <FiltersModal
          visible={filterModal.isVisible}
          tags={properties.tags || []}
          appliedFilters={queryVariables}
          onSelectTag={(tag: string) => setLastSelectedTag(tag)}
          onApply={(fields) => {
            const hasChanges =
              JSON.stringify(fields) !== JSON.stringify(queryVariables)

            const newQueryVariables = {
              ...queryVariables,
              ...fields,
              ...(hasChanges && { pagination: 1 }),
            }

            query.setAll(newQueryVariables)
            searchEvents.searchFilterApplyEvent({
              filters: newQueryVariables,
              filtersCount: getFiltersCount(newQueryVariables),
            })

            setAllFilterModal({
              isVisible: false,
              isMounted: true,
            })
          }}
          onClose={() => {
            setAllFilterModal({
              isVisible: false,
              isMounted: true,
            })
            searchEvents.searchFilterCloseEvent()
          }}
          onChange={(fields, name) =>
            searchEvents.searchFilterItemEvent(name, fields[name] as string)
          }
          onClear={() => searchEvents.searchFilterClearEvent()}
        />
      )}

      {QEVModal.isMounted && (
        <QEV
          defaultValues={QEVModal.defaultValues ?? QEVDefaultValues}
          isModalVisible={QEVModal.isVisible}
          onClose={() => {
            setQEVModal({
              isMounted: true,
              isVisible: false,
            })

            if (!hasSearchListingsResults) {
              searchEvents.searchNotFoundQEVCloseEvent({
                filters: queryVariables,
                collection: currentCollection?.name as string,
              })
              return
            }

            searchEvents.closeQEVSearchEvent()
          }}
          onCompleted={() => {
            const { filterVariables } = getVariablesProperties({
              queryParams: {},
            })

            query.setAll({
              ...filterVariables,
              citiesSlug: queryVariables.citiesSlug,
              stateSlug: queryVariables.stateSlug,
              collection: queryVariables.collection,
              pagination: 1,
            })
          }}
          successButton={{
            label: 'Ver imóveis',
            onClick: () => {
              void clearApolloStore()
              setQEVModal({
                isMounted: true,
                isVisible: false,
              })
            },
          }}
        />
      )}
      {loginModal.isMounted && (
        <LoginModal
          isModalVisible={loginModal.isVisible}
          onSuccess={() => {
            setLoginModal({
              isMounted: false,
              isVisible: false,
            })
          }}
          onClose={() =>
            setLoginModal({
              isMounted: true,
              isVisible: false,
            })
          }
          isPortal
        />
      )}
    </DefaultTemplate>
  )
}

export const getServerSideProps = async ({
  query,
  req,
}: GetServerSidePropsContext) => {
  const apolloClient = getApolloClient({ forceNew: true, cookies: req.cookies })

  const { city, lc } = query
  const { address } = parseLocation(lc as string)
  const { headers } = req || {}

  let cityFromHeader = ''
  if (headers?.[CLOUDFRONT_CITY_HEADER])
    cityFromHeader = slugify(
      decodeURIComponent(headers?.[CLOUDFRONT_CITY_HEADER] as string),
    )

  let citySlug = city || cityFromHeader || DEFAULT_CITY_SLUG

  const {
    data: { cities },
  } = (await apolloClient
    .query<CitiesQuery>({
      query: GET_CITIES,
    })
    .catch((e) => logger.capture(e))) || { data: {} }

  const coveredCity = cities?.find(
    (coverage) => coverage?.nameSlug === citySlug,
  )

  citySlug = coveredCity?.nameSlug || DEFAULT_CITY_SLUG
  const stateSlug = coveredCity?.stateSlug || DEFAULT_STATE_SLUG

  if (
    req.url?.match(/imoveis(\/\w{2})?$/) ||
    !coveredCity ||
    (!query.state && !query.city)
  ) {
    return {
      redirect: {
        destination: `/imoveis/${stateSlug}/${citySlug}`,
        statusCode: 301,
      },
    }
  }

  const searchContext = await apolloClient
    .query<GetSearchContextQuery, GetSearchContextQueryVariables>({
      query: GET_SEARCH_CONTEXT,
      variables: {
        stateSlug: coveredCity?.stateSlug,
        citySlug: coveredCity?.nameSlug,
      },
    })
    .catch((e) => logger.capture(e))

  const { neighborhood, street } =
    (await fetchGeocodeByAddress(address as string)
      .then((geocode) => (geocode ? parseGeocode(geocode) : {}))
      .catch((e) => logger.capture(e))) || {}

  const segments = (query?.filters as string)?.split('/')
  const isCoveredDistrict = searchContext?.data?.districts?.find(
    (d) => d?.nameSlug && segments?.includes(d.nameSlug),
  )

  let searchCondominiums: Array<Condominium> = []
  let searchPointsOfInterest: Array<Point> = []
  if (isCoveredDistrict) {
    const searchAddressContext = await apolloClient
      .query<
        GetSearchAddressContextQuery,
        GetSearchAddressContextQueryVariables
      >({
        query: GET_SEARCH_ADDRESS_CONTEXT,
        variables: {
          stateSlug: coveredCity?.stateSlug,
          citySlug: coveredCity?.nameSlug,
          neighborhoodSlug: isCoveredDistrict?.nameSlug,
        },
      })
      .catch((e) => logger.capture(e))
    searchCondominiums =
      (searchAddressContext?.data?.buyerContextRegions?.[0]
        ?.condominiums as Array<Condominium>) || []
    searchPointsOfInterest =
      (searchAddressContext?.data?.pointsOfInterest as Array<Point>) || []
  }

  const { searchVariables, filterVariables } = getVariablesProperties({
    queryParams: {
      ...query,
      state: stateSlug,
      city: citySlug,
      neighborhoods: slugify(neighborhood as string),
      streetSlug: slugify(street as string),
    },
    districts: searchContext?.data?.districts as Array<District>,
    tags: searchContext?.data?.tags as Array<Tag>,
    searchCollections: searchContext?.data
      ?.searchCollections as Array<Collection>,
    condominiums: searchCondominiums,
    pointsOfInterest: searchPointsOfInterest,
  })

  if (req.url?.match(/mapa[?]*/)) {
    return {
      props: {
        serverContext: {
          tags: searchContext?.data?.tags || [],
          districts: searchContext?.data?.districts || [],
          searchCollections: searchContext?.data?.searchCollections || [],
          pointsOfInterest: searchPointsOfInterest,
          condominiums: searchCondominiums,
        },
        serverCities: cities || [],
        serverFilterVariables: filterVariables,
        serverSearchVariables: searchVariables,
      },
    }
  }

  const getListings = await apolloClient
    .query<SearchListingsQuery, SearchListingsQueryVariables>({
      query: GET_SEARCH_LISTING,
      errorPolicy: 'ignore',
      variables: searchVariables,
    })
    .catch((e) => logger.capture(e))

  const getResolution = () => {
    if (filterVariables?.streetSlug) return Resolution.Address
    if (filterVariables?.neighborhoods) return Resolution.Street
    return Resolution.Neighborhood
  }

  const getRegions = await apolloClient
    .query<BuyerContextRegionsQuery, BuyerContextRegionsQueryVariables>({
      query: GET_BUYER_CONTEXT_REGIONS,
      variables: {
        citySize: 1,
        filters: {
          citySlug: filterVariables?.citiesSlug,
          neighborhoodSlug: filterVariables?.neighborhoods,
          stateSlug: filterVariables?.stateSlug,
          streetSlug: filterVariables?.streetSlug,
        },
        regionSize: 12,
        resolution: getResolution(),
      },
    })
    .catch((e) => logger.capture(e))

  return {
    props: {
      serverSearchData: getListings?.data || {},
      serverRegionsData: getRegions?.data || {},
      serverLoading: getListings?.loading || getRegions?.loading || false,
      serverContext: {
        tags: searchContext?.data?.tags || [],
        districts: searchContext?.data?.districts || [],
        searchCollections: searchContext?.data?.searchCollections || [],
        condominiums: searchCondominiums,
        pointsOfInterest: searchPointsOfInterest,
      },
      serverCities: cities || [],
      serverFilterVariables: filterVariables,
      serverSearchVariables: searchVariables,
    },
  }
}

export default memo(Search)
